import { forwardRef } from 'react'
import { PreparationLotDto } from '@services/dtos'
import { CERCLAGE_COLORS } from '@utils/constants'
import moment from 'moment'
import { SvgIcon } from '@mui/material'

interface Props {
  preparationLot: PreparationLotDto
}

const PreparationLotDymoLabel = forwardRef<HTMLDivElement, Props>(
  ({ preparationLot }, ref) => {
    const { no, compound, activityAtRefMbq, activityRefAt, preparedAt, totalVolumeMl, cerclageColor, sourceBatch, performedBy } = preparationLot
    const isotope = preparationLot.sourceElutions?.[0]?.generatorElutionInstances?.[0]?.generatorInstance?.productBatch?.productCatalog?.generatorDaughterIsotope
    const cerclage = CERCLAGE_COLORS.find((c) => c.color === cerclageColor)

    const agentLabel = performedBy
      ? `${performedBy.firstName[0].toUpperCase()}. ${performedBy.lastName}`
      : null

    const expiresAt = compound.preparationMaxAgeMinutes
      ? moment(preparedAt).add(compound.preparationMaxAgeMinutes, 'minutes')
      : null

    return (
      <div
        ref={ref}
        style={{
          position: 'fixed',
          left: '-1000rem',
          width: 336,
          height: 136,
          fontFamily: 'Arial, sans-serif',
          backgroundColor: '#fff',
          boxSizing: 'border-box',
          padding: '5px 8px 8px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        {cerclage && (
          <div
            style={{
              width: 13,
              height: 13,
              borderRadius: '50%',
              backgroundColor: cerclage.hex,
              border: '1px solid rgba(0,0,0,0.2)',
              flexShrink: 0,
            }}
          />
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', flexGrow: 1 }}>
          <div style={{ fontSize: 10, color: '#444' }}>
            Préparation <strong>#{no}</strong>
          </div>
          <SvgIcon viewBox="0 -0.5 17 17">
            <path d="M10.295 6.346 L13.029 1.159 C11.854 0.43 10.493 0.004 9.035 0.004 C7.579 0.004 6.217 0.43 5.041 1.159 L7.768 6.329 C8.141 6.127 8.562 6.003 9.014 6.003 C9.482 6.003 9.914 6.133 10.295 6.346 Z" />
            <path d="M9.676 7.552 C9.481 7.438 9.257 7.368 9.014 7.368 C8.785 7.368 8.575 7.431 8.387 7.533 C7.981 7.758 7.701 8.183 7.701 8.681 C7.701 8.709 7.709 8.736 7.711 8.764 C7.741 9.244 8.027 9.651 8.437 9.854 C8.612 9.94 8.807 9.994 9.014 9.994 C9.237 9.994 9.442 9.933 9.624 9.838 C10.018 9.629 10.289 9.233 10.318 8.765 C10.321 8.737 10.328 8.71 10.328 8.682 C10.328 8.197 10.064 7.779 9.676 7.552 Z" />
            <path d="M11.604 9.031 C11.48 9.866 10.983 10.575 10.262 10.962 L13.02 15.973 C15.329 14.544 16.893 12.055 16.993 9.032 L11.604 9.032 Z" />
            <path d="M5.028 15.973 L7.798 10.978 C7.059 10.595 6.548 9.878 6.422 9.032 L1.027 9.032 C1.127 12.055 2.703 14.544 5.028 15.973 Z" />
          </SvgIcon>
        </div>

        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#111', lineHeight: 1.1 }}>
            {compound.name}
          </span>
          {isotope && (
            <span style={{ fontSize: 11, color: '#555', fontStyle: 'italic' }}>
              {isotope.code}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 6, marginBottom: 1 }}>
          {[
            { label: 'Activité (réf.)', value: `${activityAtRefMbq} MBq` },
            { label: 'Mesure le', value: moment(activityRefAt).format('DD/MM/YY HH:mm') },
            ...(totalVolumeMl !== null ? [{ label: 'Volume', value: `${totalVolumeMl} mL` }] : []),
            ...(sourceBatch?.lotNumber ? [{ label: 'Lot kit', value: sourceBatch.lotNumber }] : []),
          ].map(({ label, value }) => (
            <div key={label}>
              <div style={{ fontSize: 7, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#111' }}>{value}</div>
            </div>
          ))}
        </div>

        <div style={{ borderTop: '1px solid #ddd', paddingTop: 3, marginTop: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 9, color: '#555' }}>
              <strong>{moment(preparedAt).format('DD/MM/YYYY HH:mm')}</strong>
            </span>
            {agentLabel && (
              <strong style={{ fontSize: 9, color: '#555' }}>{agentLabel}</strong>
            )}
          </div>
          {expiresAt && (
            <span style={{ fontSize: 9, color: '#c00', fontWeight: 600 }}>
              Péremption{' '}{expiresAt.format('DD/MM/YYYY HH:mm')}
            </span>
          )}
        </div>
      </div>
    )
  },
)

export default PreparationLotDymoLabel
